import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from '../utils/safeToast';
import { 
  Settings, 
  Mail, 
  Save, 
  Loader2, 
  CheckCircle, 
  XCircle,
  TestTube,
  Eye,
  EyeOff,
  MessageSquare,
  AlertTriangle,
  Calendar,
  FileText,
  Send,
  Clock
} from 'lucide-react';

const API = "/api";

const SettingsPage = () => {
  // E-Mail-Konfiguration
  const [emailConfig, setEmailConfig] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    mailbox_user: '',
    client_secret_expires: ''
  });
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailUpdatedAt, setEmailUpdatedAt] = useState(null);
  const [emailUpdatedBy, setEmailUpdatedBy] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // E-Mail-Signatur
  const [signature, setSignature] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);

  // Abreise-Texte
  const [departureTexts, setDepartureTexts] = useState({
    form_title: 'Abreise mitteilen',
    form_description: 'Die reguläre Abreise ist um 10:00 Uhr. Wenn Sie früher abreisen, freut sich unser Reinigungsteam über diese Information.',
    email_subject_template: 'Abreise gemeldet für {date}, {time} – {property_name}',
    email_body_template: 'Der Gast der Wohnung {property_name} meldet seine Abreise für den {date} um {time}.\n\nDie Wohnung ist ab diesem Zeitpunkt für die Endreinigung frei.',
    confirmation_message: 'Vielen Dank. Ihre Abreiseinformation wurde übermittelt.',
    thank_you_note: 'Vielen Dank für Ihre Unterstützung – das hilft unserem Reinigungsteam sehr.'
  });
  const [savingDeparture, setSavingDeparture] = useState(false);

  // Täglicher Bericht
  const [reportPreview, setReportPreview] = useState(null);
  const [loadingReportPreview, setLoadingReportPreview] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const [loading, setLoading] = useState(true);

  // Berechne Tage bis Ablauf
  const getDaysUntilExpiry = () => {
    if (!emailConfig.client_secret_expires) return null;
    const expiryDate = new Date(emailConfig.client_secret_expires);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiry = getDaysUntilExpiry();
  const showExpiryWarning = daysUntilExpiry !== null && daysUntilExpiry <= 60;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [emailRes, signatureRes, departureRes] = await Promise.all([
        axios.get(`${API}/settings/email-config`),
        axios.get(`${API}/settings/email-signature`),
        axios.get(`${API}/settings/departure-texts`).catch(() => ({ data: {} }))
      ]);

      // E-Mail Config
      if (emailRes.data.configured) {
        setEmailConfig({
          tenant_id: emailRes.data.tenant_id || '',
          client_id: emailRes.data.client_id || '',
          client_secret: '', // Secret wird nicht zurückgegeben
          mailbox_user: emailRes.data.mailbox_user || '',
          client_secret_expires: emailRes.data.client_secret_expires || ''
        });
        setEmailConfigured(true);
        setEmailUpdatedAt(emailRes.data.updated_at);
        setEmailUpdatedBy(emailRes.data.updated_by);
      }

      setSignature(signatureRes.data.text);
      
      // Abreise-Texte (mit Defaults falls nicht gesetzt)
      if (departureRes.data && Object.keys(departureRes.data).length > 0) {
        setDepartureTexts(prev => ({ ...prev, ...departureRes.data }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    if (!emailConfig.tenant_id || !emailConfig.client_id || !emailConfig.client_secret || !emailConfig.mailbox_user) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setSavingEmail(true);
    setTestResult(null);
    try {
      await axios.post(`${API}/settings/email-config`, emailConfig);
      toast.success('E-Mail-Konfiguration gespeichert');
      setEmailConfigured(true);
      setEmailUpdatedAt(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmailConfig = async () => {
    setTestingEmail(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/email-config/test`);
      setTestResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Testen';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    try {
      await axios.post(`${API}/settings/email-signature`, { text: signature });
      toast.success('Signatur gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSaveDepartureTexts = async () => {
    setSavingDeparture(true);
    try {
      await axios.post(`${API}/settings/departure-texts`, departureTexts);
      toast.success('Abreise-Texte gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingDeparture(false);
    }
  };

  // Täglicher Bericht - Vorschau laden
  const handleLoadReportPreview = async () => {
    setLoadingReportPreview(true);
    try {
      const res = await axios.get(`${API}/settings/daily-report/preview`);
      setReportPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoadingReportPreview(false);
    }
  };

  // Täglicher Bericht - Jetzt senden
  const handleSendReportNow = async () => {
    setSendingReport(true);
    try {
      const res = await axios.post(`${API}/settings/daily-report/send`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <Settings className="w-8 h-8 text-[#0E4C92]" />
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
          Einstellungen
        </h1>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            E-Mail (Microsoft Graph)
          </TabsTrigger>
          <TabsTrigger value="texts" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            E-Mail-Signatur
          </TabsTrigger>
          <TabsTrigger value="departure" className="gap-2">
            <Calendar className="w-4 h-4" />
            Abreise-Meldung
          </TabsTrigger>
          <TabsTrigger value="daily-report" className="gap-2">
            <FileText className="w-4 h-4" />
            Täglicher Bericht
          </TabsTrigger>
        </TabsList>

        {/* E-Mail-Konfiguration Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Microsoft Graph E-Mail-Konfiguration
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Konfigurieren Sie hier die Microsoft Azure AD-Zugangsdaten für den E-Mail-Versand
                  </CardDescription>
                </div>
                {emailConfigured ? (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Konfiguriert
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <XCircle className="w-3 h-3 mr-1" />
                    Nicht konfiguriert
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="tenant_id">Tenant ID (Verzeichnis-ID)</Label>
                  <Input
                    id="tenant_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={emailConfig.tenant_id}
                    onChange={(e) => setEmailConfig({ ...emailConfig, tenant_id: e.target.value })}
                    data-testid="tenant-id-input"
                  />
                  <p className="text-xs text-stone-500">
                    Azure Portal → Azure Active Directory → Übersicht
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID (Anwendungs-ID)</Label>
                  <Input
                    id="client_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={emailConfig.client_id}
                    onChange={(e) => setEmailConfig({ ...emailConfig, client_id: e.target.value })}
                    data-testid="client-id-input"
                  />
                  <p className="text-xs text-stone-500">
                    Azure Portal → App-Registrierungen → Ihre App
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_secret">Client Secret (Geheimer Clientschlüssel)</Label>
                  <div className="relative">
                    <Input
                      id="client_secret"
                      type={showSecret ? 'text' : 'password'}
                      placeholder={emailConfigured ? '••••••••••••••••' : 'Geheimen Schlüssel eingeben'}
                      value={emailConfig.client_secret}
                      onChange={(e) => setEmailConfig({ ...emailConfig, client_secret: e.target.value })}
                      className="pr-10"
                      data-testid="client-secret-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-stone-500">
                    Azure Portal → App-Registrierungen → Zertifikate & Geheimnisse
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_secret_expires">Client-Secret Gültig bis</Label>
                  <div className="relative">
                    <Input
                      id="client_secret_expires"
                      type="date"
                      value={emailConfig.client_secret_expires ? emailConfig.client_secret_expires.split('T')[0] : ''}
                      onChange={(e) => setEmailConfig({ ...emailConfig, client_secret_expires: e.target.value })}
                      className="pl-10"
                      data-testid="client-secret-expires-input"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  </div>
                  {daysUntilExpiry !== null && (
                    <p className={`text-xs ${showExpiryWarning ? 'text-amber-600 font-medium' : 'text-stone-500'}`}>
                      {daysUntilExpiry > 0 
                        ? `Noch ${daysUntilExpiry} Tage gültig`
                        : daysUntilExpiry === 0 
                          ? 'Läuft heute ab!'
                          : `Seit ${Math.abs(daysUntilExpiry)} Tagen abgelaufen!`
                      }
                      {showExpiryWarning && daysUntilExpiry > 0 && ' – Bitte rechtzeitig erneuern'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mailbox_user">Absender E-Mail-Adresse</Label>
                  <Input
                    id="mailbox_user"
                    type="email"
                    placeholder="info@ihre-domain.de"
                    value={emailConfig.mailbox_user}
                    onChange={(e) => setEmailConfig({ ...emailConfig, mailbox_user: e.target.value })}
                    data-testid="mailbox-user-input"
                  />
                  <p className="text-xs text-stone-500">
                    Die E-Mail-Adresse, von der aus E-Mails gesendet werden
                  </p>
                </div>
              </div>

              {/* Test-Ergebnis */}
              {testResult && (
                <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                      {testResult.message}
                    </span>
                  </div>
                </div>
              )}

              {/* Metadaten */}
              {emailUpdatedAt && (
                <p className="text-xs text-stone-400">
                  Zuletzt aktualisiert: {new Date(emailUpdatedAt).toLocaleString('de-DE')}
                  {emailUpdatedBy && ` von ${emailUpdatedBy}`}
                </p>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={handleSaveEmailConfig} 
                  disabled={savingEmail}
                  data-testid="save-email-config-btn"
                >
                  {savingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Konfiguration speichern
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTestEmailConfig}
                  disabled={testingEmail || !emailConfigured}
                  data-testid="test-email-config-btn"
                >
                  {testingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Verbindung testen
                </Button>
              </div>

              {/* Hilfe-Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="font-medium text-blue-900 mb-2">So erhalten Sie die Zugangsdaten:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Melden Sie sich im <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a> an</li>
                  <li>Navigieren Sie zu &quot;Azure Active Directory&quot; → &quot;App-Registrierungen&quot;</li>
                  <li>Erstellen Sie eine neue App oder wählen Sie eine bestehende</li>
                  <li>Kopieren Sie die Anwendungs-ID (Client ID) und Verzeichnis-ID (Tenant ID)</li>
                  <li>Erstellen Sie unter &quot;Zertifikate &amp; Geheimnisse&quot; ein neues Client Secret</li>
                  <li>Fügen Sie unter &quot;API-Berechtigungen&quot; die Berechtigung &quot;Mail.Send&quot; hinzu</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-Mail-Signatur Tab */}
        <TabsContent value="texts" className="space-y-6">
          {/* E-Mail-Signatur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                E-Mail-Signatur
              </CardTitle>
              <CardDescription>
                Diese Signatur wird an generierte Texte angehängt.
                Platzhalter: {"{{mitarbeiter_name}}"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                rows={4}
                className="font-mono text-sm"
                data-testid="signature-input"
              />
              <Button onClick={handleSaveSignature} disabled={savingSignature}>
                {savingSignature ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Signatur speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Abreise-Meldung Tab */}
        <TabsContent value="departure" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Abreise-Meldung Texte
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie hier die Texte für das Abreise-Formular und die automatischen E-Mails an die Reinigungsfirma.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Formular-Texte */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Formular-Texte</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="departure-form-title">Überschrift des Formulars</Label>
                  <Input
                    id="departure-form-title"
                    value={departureTexts.form_title}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, form_title: e.target.value })}
                    placeholder="Abreise mitteilen"
                    data-testid="departure-form-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure-form-description">Erklärungstext im Formular</Label>
                  <Textarea
                    id="departure-form-description"
                    value={departureTexts.form_description}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, form_description: e.target.value })}
                    rows={3}
                    placeholder="Die reguläre Abreise ist um 10:00 Uhr..."
                    data-testid="departure-form-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure-confirmation">Bestätigungsnachricht nach Absenden</Label>
                  <Input
                    id="departure-confirmation"
                    value={departureTexts.confirmation_message}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, confirmation_message: e.target.value })}
                    placeholder="Vielen Dank. Ihre Abreiseinformation wurde übermittelt."
                    data-testid="departure-confirmation"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure-thank-you">Dankes-Hinweis</Label>
                  <Input
                    id="departure-thank-you"
                    value={departureTexts.thank_you_note}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, thank_you_note: e.target.value })}
                    placeholder="Vielen Dank für Ihre Unterstützung..."
                    data-testid="departure-thank-you"
                  />
                </div>
              </div>

              {/* E-Mail-Texte */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-slate-900">E-Mail an Reinigungsfirma</h3>
                
                {/* Hinweisbox für Platzhalter */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Verfügbare Platzhalter:</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-white px-2 py-1 rounded border border-blue-100">
                      <code className="text-blue-700">{'{date}'}</code>
                      <span className="text-blue-600 text-xs ml-1">→ 20.01.2025</span>
                    </div>
                    <div className="bg-white px-2 py-1 rounded border border-blue-100">
                      <code className="text-blue-700">{'{time}'}</code>
                      <span className="text-blue-600 text-xs ml-1">→ 06:00</span>
                    </div>
                    <div className="bg-white px-2 py-1 rounded border border-blue-100">
                      <code className="text-blue-700">{'{property_name}'}</code>
                      <span className="text-blue-600 text-xs ml-1">→ Objektname</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure-email-subject" className="text-base font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#0E4C92]" />
                    E-Mail-Betreff – Abreise
                  </Label>
                  <Input
                    id="departure-email-subject"
                    value={departureTexts.email_subject_template}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, email_subject_template: e.target.value })}
                    placeholder="Abreise gemeldet für {date}, {time} – {property_name}"
                    className="font-mono text-sm"
                    data-testid="departure-email-subject"
                  />
                  <p className="text-xs text-slate-500">
                    Beispiel: <span className="font-mono bg-slate-100 px-1 rounded">Abreise gemeldet für 20.01.2025, 06:00 – Testobjekt Emergent</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure-email-body">E-Mail-Text</Label>
                  <Textarea
                    id="departure-email-body"
                    value={departureTexts.email_body_template}
                    onChange={(e) => setDepartureTexts({ ...departureTexts, email_body_template: e.target.value })}
                    rows={5}
                    placeholder="Der Gast der Wohnung {property_name} meldet seine Abreise..."
                    data-testid="departure-email-body"
                  />
                </div>
              </div>

              <Button onClick={handleSaveDepartureTexts} disabled={savingDeparture} className="bg-[#0E4C92] hover:bg-[#0A3A70]">
                {savingDeparture ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Abreise-Texte speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Täglicher Bericht Tab */}
        <TabsContent value="daily-report">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Täglicher Aufgaben-Bericht
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Automatischer E-Mail-Bericht mit neuen und offenen Aufgaben
                  </CardDescription>
                </div>
                <Badge className="bg-blue-100 text-blue-700 border-0">
                  <Clock className="w-3 h-3 mr-1" />
                  Täglich 07:00 Uhr
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info-Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-medium text-blue-900">Empfänger: info@gb-sylt.de</p>
                    <p className="text-sm text-blue-700">
                      Der Bericht wird täglich um 07:00 Uhr automatisch versendet und enthält:
                    </p>
                    <ul className="text-sm text-blue-700 list-disc list-inside mt-2 space-y-1">
                      <li>Neue Aufgaben der letzten 24 Stunden</li>
                      <li>Alle offenen Aufgaben (Status: neu oder in Bearbeitung)</li>
                      <li>Überfällige Aufgaben (älter als 7 Tage ohne Bearbeitung)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Voraussetzung */}
              {!emailConfigured && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">Microsoft Graph nicht konfiguriert</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Für den automatischen Berichtsversand muss zuerst die E-Mail-Konfiguration (Microsoft Graph) eingerichtet werden.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Aktionen */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleLoadReportPreview} 
                  disabled={loadingReportPreview}
                  variant="outline"
                  data-testid="preview-report-btn"
                >
                  {loadingReportPreview ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Vorschau laden
                </Button>
                
                <Button 
                  onClick={handleSendReportNow} 
                  disabled={sendingReport || !emailConfigured}
                  className="bg-[#0E4C92] hover:bg-[#0A3A70]"
                  data-testid="send-report-btn"
                >
                  {sendingReport ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Bericht jetzt senden
                </Button>
              </div>

              {/* Vorschau */}
              {reportPreview && (
                <div className="space-y-4">
                  {/* Statistiken */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-green-700">{reportPreview.statistics.new_tasks}</div>
                      <div className="text-xs text-green-600">Neue (24h)</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">{reportPreview.statistics.open_tasks}</div>
                      <div className="text-xs text-blue-600">Offen gesamt</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-700">{reportPreview.statistics.in_progress}</div>
                      <div className="text-xs text-amber-600">In Bearbeitung</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-700">{reportPreview.statistics.overdue_tasks}</div>
                      <div className="text-xs text-red-600">Überfällig (7+ Tage)</div>
                    </div>
                  </div>

                  {/* HTML Vorschau */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      E-Mail-Vorschau
                    </div>
                    <div 
                      className="p-4 bg-white max-h-[500px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: reportPreview.html_preview }}
                    />
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Vorschau generiert: {new Date(reportPreview.generated_at).toLocaleString('de-DE')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
